import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Clock, Trash } from "@phosphor-icons/react";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";

const fmtMinutes = (m) => {
  const mins = Math.max(0, parseInt(m, 10) || 0);
  const h = Math.floor(mins / 60);
  const rem = mins % 60;
  if (h && rem) return `${h}h ${rem}m`;
  if (h) return `${h}h`;
  return `${rem}m`;
};

/**
 * §6 — Timewriting per ticket. Agent-facing widget on the ticket
 * sidebar. Log minutes + optional note per session; totals roll up
 * on the ticket and feed `/api/analytics/timewriting`.
 */
export function TimewritingCard({ ticketId, currentUserId, isAdmin }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get(`/tickets/${ticketId}/time-entries`);
      setEntries(r.data.entries || []);
      setTotal(r.data.total_minutes || 0);
    } catch (e) {
      setEntries([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // §6 — Multi-person rollup for the header. Any number of team
  // members can log time on the same ticket (common for onboarding
  // tickets that span IT / HR / Finance). Groups entries by user
  // and renders a compact "Alice 45m · Bob 15m" summary.
  const byUser = entries.reduce((acc, e) => {
    const key = e.user_id || "unknown";
    if (!acc[key]) acc[key] = { name: e.user_name || "Unknown", minutes: 0 };
    acc[key].minutes += e.minutes || 0;
    return acc;
  }, {});
  const contributors = Object.values(byUser).sort((a, b) => b.minutes - a.minutes);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ticketId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const m = parseInt(minutes, 10);
    if (!m || m <= 0) {
      toast.error("Minutes must be greater than zero");
      return;
    }
    if (m > 24 * 60) {
      toast.error("Max 24h (1440 minutes) per entry");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/tickets/${ticketId}/time-entries`, {
        minutes: m,
        note: note.trim() || undefined,
      });
      setMinutes(""); setNote("");
      toast.success(`Logged ${fmtMinutes(m)}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to log time");
    } finally { setSaving(false); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete ${fmtMinutes(entry.minutes)} logged on ${entry.entry_date}?`)) return;
    setDeletingId(entry.id);
    try {
      await api.delete(`/tickets/${ticketId}/time-entries/${entry.id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete entry");
    } finally { setDeletingId(null); }
  };

  return (
    <div
      className="hairline rounded-lg p-3 bg-white space-y-3"
      data-testid="timewriting-card"
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium flex items-center gap-1">
          <Clock size={11} weight="duotone" /> Time logged
        </div>
        <div className="font-mono text-xs tabular-nums text-slate-700" data-testid="timewriting-total">
          {fmtMinutes(total)}
        </div>
      </div>
      {contributors.length > 1 && (
        <div
          className="text-[10px] text-slate-500 flex flex-wrap gap-x-2"
          data-testid="timewriting-contributors"
        >
          {contributors.map((c) => (
            <span key={c.name}>
              <span className="text-slate-700 font-medium">{c.name}</span>{" "}
              <span className="tabular-nums">{fmtMinutes(c.minutes)}</span>
            </span>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="space-y-1.5" data-testid="timewriting-form">
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            min={1}
            max={1440}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            placeholder="Minutes"
            className="h-8 w-20 text-sm"
            data-testid="timewriting-minutes-input"
          />
          <div className="flex items-center gap-1 text-[10px]">
            {[15, 30, 60].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMinutes(String(m))}
                data-testid={`timewriting-quick-${m}`}
                className={`px-1.5 py-0.5 rounded-full border transition ${
                  String(m) === minutes
                    ? "bg-[#4f4c90] text-white border-[#4f4c90]"
                    : "text-slate-500 border-slate-200 hover:border-[#4f4c90]/50 hover:text-[#4f4c90]"
                }`}
              >
                {m < 60 ? `${m}m` : `${m / 60}h`}
              </button>
            ))}
          </div>
          <Button
            type="submit"
            disabled={saving || !minutes}
            className="h-8 px-2.5 text-[11px] ml-auto bg-[#4f4c90] hover:bg-[#5F5CA5]"
            data-testid="timewriting-log-btn"
          >
            {saving ? "…" : "Log"}
          </Button>
        </div>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What did you do? (optional)"
          className="h-8 w-full text-sm"
          data-testid="timewriting-note-input"
        />
      </form>

      {loading ? null : entries.length === 0 ? (
        <div className="text-[11px] text-slate-500">No time logged yet.</div>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
          {entries.map((e) => {
            const canDelete = isAdmin || e.user_id === currentUserId;
            return (
              <li
                key={e.id}
                data-testid={`timewriting-entry-${e.id}`}
                className="flex items-center justify-between gap-2 text-xs py-1 border-b border-slate-100 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono tabular-nums text-slate-800">{fmtMinutes(e.minutes)}</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-600 truncate">{e.user_name}</span>
                    <span className="text-slate-400 text-[10px]">{timeAgo(e.created_at)}</span>
                  </div>
                  {e.note && <div className="text-slate-500 text-[11px] truncate">{e.note}</div>}
                </div>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => remove(e)}
                    disabled={deletingId === e.id}
                    data-testid={`timewriting-delete-${e.id}`}
                    className="text-slate-400 hover:text-rose-600"
                    aria-label="Delete entry"
                  >
                    <Trash size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
